import type { actions } from '@luxuryescapes/contract-flights-charter';

import type { CharterInventoryFilters, CharterInventoryFlight } from '~/components/Flights/Charter/charter.types';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1`;

export type CharterDetailsGetRes =
  actions['reservationGetDetailsByPnr']['responses']['200']['content']['application/json'];

export const fetchCharterDetails = async (pnr: string): Promise<CharterDetailsGetRes['result']> => {
  const uri = `${BASE_PATH}/reservations/${pnr.trim().toUpperCase()}/details`;
  const response: CharterDetailsGetRes = await request(uri, { method: 'GET' });
  return response.result;
};

export type CharterReservationUpdateRes =
  actions['reservationUpdate']['responses']['200']['content']['application/json'];
export type CharterReservationReqBody = actions['reservationUpdate']['parameters']['body']['ReservationUpdatePayload'];

export const charterReservationUpdate = async (
  payload: CharterReservationReqBody & { pnr: string },
): Promise<CharterReservationUpdateRes> => {
  const uri = `${BASE_PATH}/reservations/${payload.pnr.trim().toUpperCase()}`;
  return request(uri, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export type CharterInventoryGetRes =
  actions['departuresGetInventory']['responses']['200']['content']['application/json'];

export const fetchCharterInventory = async (
  filters: CharterInventoryFilters,
): Promise<Array<CharterInventoryFlight>> => {
  const uri = `${BASE_PATH}/departures/inventory`;
  const response: CharterInventoryGetRes = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(filters),
  });

  return response.result;
};

/** Response type for fare family pricing GET */
export type FareFamilyPricingGetRes =
  actions['getFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Query parameters for fare family pricing GET */
export interface FareFamilyPricingParams {
  departingDepAirport: string;
  departingArrAirport: string;
  returningDepAirport: string;
  returningArrAirport: string;
  offset?: number;
  limit?: number;
}

/**
 * Fetches fare family pricing for charter flights by route.
 * Returns pricing data for both departing and returning flights.
 */
export const fetchCharterFareFamilyPricing = async (
  params: FareFamilyPricingParams,
): Promise<FareFamilyPricingGetRes> => {
  const queryParams = new URLSearchParams({
    departingDepAirport: params.departingDepAirport,
    departingArrAirport: params.departingArrAirport,
    returningDepAirport: params.returningDepAirport,
    returningArrAirport: params.returningArrAirport,
  });

  if (params.offset !== undefined) {
    queryParams.set('offset', String(params.offset));
  }
  if (params.limit !== undefined) {
    queryParams.set('limit', String(params.limit));
  }

  const uri = `${BASE_PATH}/fare-families/pricing?${queryParams.toString()}`;
  const response: FareFamilyPricingGetRes = await request(uri, { method: 'GET' });
  return response;
};

/** Response type for fare family pricing PUT */
export type FareFamilyPricingUpdateRes =
  actions['updateFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Payload type for fare family pricing PUT (parallel arrays) */
export type FareFamilyPricingUpdatePayload = actions['updateFareFamilyPricing']['parameters']['body']['payload'];

/**
 * Bulk updates fare family pricing for charter flights.
 * Accepts parallel arrays of pricing data keyed by flightInstanceIds.
 */
export const updateCharterFareFamilyPricing = async (
  payload: FareFamilyPricingUpdatePayload,
): Promise<FareFamilyPricingUpdateRes> => {
  const uri = `${BASE_PATH}/fare-families/pricing`;
  const response: FareFamilyPricingUpdateRes = await request(uri, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response;
};

/** Response type for fare family pricing preview POST */
export type FareFamilyPricingPreviewRes =
  actions['previewFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Payload type for fare family pricing preview POST */
export type FareFamilyPricingPreviewPayload = actions['previewFareFamilyPricing']['parameters']['body']['payload'];

/**
 * Previews fare family pricing calculations without persisting to database.
 */
export const previewCharterFareFamilyPricing = async (
  payload: FareFamilyPricingPreviewPayload,
): Promise<FareFamilyPricingPreviewRes> => {
  const uri = `${BASE_PATH}/fare-families/pricing/preview`;
  const response: FareFamilyPricingPreviewRes = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response;
};
